import {useEffect, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {AmberDot} from '@/components/amber-dot'
import {HelpIcon} from '@/components/help-icon'
import {PilaDeRecortes} from '@/components/bateria/cola/pila-de-recortes'
import {IndicadorDeTransito} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {SIN_RESPUESTA, type ModoRecorte} from '@/lib/bateria'
import {cn} from '@/lib/utils'

export type OpcionCasilla = {
  valor: string
  etiqueta: string
  pct: number
}

type TiraOpcionesProps = {
  cropUrl: string
  /** La hoja del escaneo, en el sitio del recorte cuando no hay recorte de fila. */
  papel?: ReactNode
  /** Corta el clic y el atajo: la tira monta su propio manejador global de teclado. */
  deshabilitada?: boolean
  /** Mientras el servidor corta la fila no se pinta el aviso de degradado, que aún no es cierto. */
  evidenciaEnCurso?: boolean
  /** Rótulo dentro de la casilla propuesta («leyó», «tinta»): una pista de tinta no es una lectura. */
  propuestaEtiqueta?: string
  opciones: OpcionCasilla[]
  seleccion: string | null
  onElegir: (valor: string | null) => void
  modo: ModoRecorte
  /** Casillas que señala el gesto en curso (tachón, doble marca); nunca es una selección. */
  marcadas?: readonly string[]
  /** Casilla que propone el motor; `activa` y `señalada` ganan sobre ella en la misma casilla. */
  propuesta?: string | null
  /** Acción de cierre junto a «En blanco», p. ej. resolver un tachón sin respuesta válida. */
  extra?: ReactNode
}

function esCampoDeTexto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

/** Una sola definición para la tira y la ficha. Verde sólido lo decidió una persona y ámbar
 * punteado lo leyó la máquina: la propuesta nunca va sólida ni verde. */
export const OPCION_PROPUESTA = 'border-2 border-dashed border-tone-amber-fg bg-tone-amber-bg text-tone-amber-fg'
export const OPCION_PROPUESTA_TENUE = 'border-dashed border-tone-amber-fg'

/** El punto ámbar solo no basta (también marca una casilla señalada): la palabra separa «leyó» de «tinta». */
export function MarcaDelSistema({etiqueta, testId}: {etiqueta: string; testId?: string}) {
  const {t} = useTranslation()
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold tracking-wider uppercase">
      <AmberDot label={t('common.senal.propuestaDelSistema')} className="h-1.5 w-1.5" />
      {etiqueta}
    </span>
  )
}

/* Fuera de `PilaDeRecortesProvider` cae a una pila de un solo `<img>`. */
export const CropImage = PilaDeRecortes

export const OPCION_BASE =
  'rounded-md border px-2 py-1.5 text-xs font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50'
export const OPCION_ON = 'border-primary bg-primary text-primary-foreground shadow-card'
export const OPCION_OFF =
  'border-input bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'

/** Compartido por el mapa de teclas y la tira para que no diverjan. */
export function AtajoNumero({n}: {n: number}) {
  return (
    <span aria-hidden="true" className="font-mono opacity-60">
      {n}
    </span>
  )
}

/** Por debajo de esta separación (en % del recorte) dos marcas se pisan; detecta los `pct` en cero. */
const SEPARACION_MINIMA_PCT = 2

/** Igual que `opciones_pct_de_banda` del motor: sin `pct` estrictamente crecientes no se señala (§3.4). */
function geometriaUtilizable(opciones: OpcionCasilla[]): boolean {
  if (opciones.length < 2) return false
  let previo = -Infinity
  for (const {pct} of opciones) {
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return false
    if (previo !== -Infinity && pct - previo < SEPARACION_MINIMA_PCT) return false
    previo = pct
  }
  return true
}

/** §3.5 de docs/modulos/bateria-confirmacion-spec.md. La guía señala en % porque el recorte escala;
 * los botones no se posicionan porque con casillas a ~8 % del ancho se pisarían. */
export function TiraOpciones({
  cropUrl,
  opciones,
  seleccion,
  onElegir,
  modo,
  marcadas,
  propuesta,
  extra,
  papel,
  deshabilitada = false,
  evidenciaEnCurso = false,
  propuestaEtiqueta,
}: TiraOpcionesProps) {
  const {t} = useTranslation()
  const conGuia = modo === 'fila' && geometriaUtilizable(opciones)

  function elegir(valor: string) {
    if (deshabilitada) return
    onElegir(seleccion === valor ? null : valor)
  }

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || esCampoDeTexto(e.target)) return
      if (deshabilitada) return
      if (e.key === '0') {
        e.preventDefault()
        elegir(SIN_RESPUESTA)
        return
      }
      const n = Number(e.key)
      if (Number.isInteger(n) && n >= 1 && n <= opciones.length) {
        e.preventDefault()
        elegir(opciones[n - 1].valor)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones, seleccion, onElegir, deshabilitada])

  return (
    <div className="space-y-2" data-testid="tira-opciones">
      {/* Guía y recorte miden su % contra la misma caja: nada con padding o borde entre ellos. */}
      <div className="relative rounded-lg border border-border bg-secondary/40 p-2">
        {/* Fuera de la cola no hay proveedor de interlock y no pinta nada. */}
        <IndicadorDeTransito />
        {papel ?? (
          <CropImage
            url={cropUrl}
            alt={t('admin.bateria.tiraOpciones.cropAlt')}
            fallback={t('admin.bateria.tiraOpciones.noCrop')}
          />
        )}
        {conGuia && (
          <div className="relative mt-1 h-4 w-full" aria-hidden="true" data-testid="tira-opciones-guia">
            {opciones.map((opcion, i) => (
              <span
                key={opcion.valor}
                data-testid={`tira-guia-${opcion.valor}`}
                style={{left: `${opcion.pct}%`}}
                className="absolute top-0 -translate-x-1/2 font-mono text-[11px] leading-4 text-muted-foreground">
                {i + 1}
              </span>
            ))}
          </div>
        )}
        {modo !== 'fila' && !evidenciaEnCurso && (
          <p
            className="notice-amber mt-2 flex items-center gap-1.5 rounded-md px-3 py-2 font-mono text-[11px] tracking-wide"
            data-testid="tira-opciones-degradado">
            {modo === 'ventana' ? (
              <>
                {t('admin.bateria.tiraOpciones.avisoVentana')}
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('admin.bateria.tiraOpciones.avisoVentana')})}
                  testId="tira-opciones-ventana-tip">
                  {t('admin.bateria.tiraOpciones.avisoVentanaTip')}
                </HelpIcon>
              </>
            ) : (
              t('admin.bateria.tiraOpciones.avisoPagina')
            )}
          </p>
        )}
        <div
          role="radiogroup"
          aria-label={t('admin.bateria.tiraOpciones.grupoLabel')}
          className="mt-2 flex flex-wrap gap-1.5"
          data-testid="tira-opciones-grupo">
          {opciones.map((opcion, i) => {
            const activa = seleccion === opcion.valor
            const señalada = marcadas?.includes(opcion.valor) ?? false
            // Notoria sin selección y tenue si se eligió otra; `activa` y `señalada` ganan siempre.
            const esPropuesta = !señalada && propuesta === opcion.valor && !activa
            const propuestaViva = esPropuesta && seleccion === null
            return (
              <button
                key={opcion.valor}
                type="button"
                role="radio"
                aria-checked={activa}
                aria-keyshortcuts={i < 9 && !deshabilitada ? String(i + 1) : undefined}
                data-testid={`tira-opcion-${opcion.valor}`}
                data-senalada={señalada ? 'true' : undefined}
                data-propuesta={propuestaViva ? 'true' : esPropuesta ? 'tenue' : undefined}
                disabled={deshabilitada}
                title={señalada ? t('admin.bateria.tiraOpciones.casillaSenalada') : undefined}
                onClick={() => elegir(opcion.valor)}
                className={cn(
                  OPCION_BASE,
                  'inline-flex min-h-11 items-center gap-1 md:min-h-0 disabled:cursor-default',
                  activa ? OPCION_ON : OPCION_OFF,
                  señalada && 'border-tone-amber-fg',
                  propuestaViva && OPCION_PROPUESTA,
                  esPropuesta && !propuestaViva && OPCION_PROPUESTA_TENUE
                )}>
                {i < 9 && <AtajoNumero n={i + 1} />}
                {señalada && <AmberDot label={t('common.senal.senalada')} testId={`tira-senalada-${opcion.valor}`} />}
                {propuestaViva && propuestaEtiqueta !== undefined && (
                  <MarcaDelSistema etiqueta={propuestaEtiqueta} testId={`tira-propuesta-${opcion.valor}`} />
                )}
                {activa && <span aria-hidden="true">✓</span>}
                {opcion.etiqueta}
              </button>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          role="radio"
          aria-checked={seleccion === SIN_RESPUESTA}
          aria-keyshortcuts="0"
          data-testid="tira-opcion-en-blanco"
          disabled={deshabilitada}
          onClick={() => elegir(SIN_RESPUESTA)}
          className={cn(
            OPCION_BASE,
            'inline-flex min-h-11 items-center gap-1 border-dashed md:min-h-0',
            seleccion === SIN_RESPUESTA ? OPCION_ON : OPCION_OFF
          )}>
          <AtajoNumero n={0} />
          {seleccion === SIN_RESPUESTA && <span aria-hidden="true">✓</span>}
          {t('admin.bateria.tiraOpciones.enBlanco')}
        </button>
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('admin.bateria.tiraOpciones.enBlanco')})}
          testId="tira-opcion-en-blanco-tip">
          {t('admin.bateria.tiraOpciones.enBlancoAyuda')}
        </HelpIcon>
        {extra}
      </div>
    </div>
  )
}
